import styled, { css } from "styled-components";
import { TAP_TARGET, mediaPhone } from "@/shared/theme/breakpoints";

/**
 * Phone sizing for the editable controls inside a creator card (List, Outreach, Grouping). Spread inside a
 * `mediaPhone` block on the card: antd selects and text inputs become 44px tall with 16px text, so they are
 * tappable and iOS doesn't zoom on focus. The doubled `.ant-select` outranks `StyledSelect`'s `&&` rules.
 */
export const creatorCardPhoneControls = css`
	.ant-select.ant-select,
	.ant-select.ant-select .ant-select-selector {
		height: ${TAP_TARGET}px !important;
	}

	.ant-select.ant-select .ant-select-selection-item,
	.ant-select.ant-select .ant-select-selection-placeholder {
		font-size: 16px;
		line-height: ${TAP_TARGET - 6}px !important;
		height: ${TAP_TARGET - 6}px !important;
	}

	input[type="text"],
	input[type="email"],
	input:not([type]) {
		font-size: 16px;
	}
`;

/** The desktop creator card's minimum width: the List/Outreach and Grouping grids lay cards out at least this wide. */
export const CREATOR_CARD_MIN_WIDTH = 370;

/** Makes a creator card a size container, so its fields can react to the card's own width. */
export const creatorCardContainer = css`
	container: creator-card / inline-size;
`;

/**
 * Styles for a card narrower than the desktop minimum (phones, or any squeezed container): each field's
 * label moves above its value. A container query, so it follows the card's width, not the viewport's.
 */
export const mediaNarrowCard = (...args: Parameters<typeof css>) => css`
	@container creator-card (max-width: ${CREATOR_CARD_MIN_WIDTH - 1}px) {
		${css(...args)}
	}
`;

/**
 * Tallest a card gets when it sizes to its content (one column, phones). Longer tabs, like a platform's
 * metrics, scroll inside the card body; the header, tabs and footer stay in view.
 */
export const CREATOR_CARD_MAX_HEIGHT = 560;

/** Gap between cards in the creator grids (desktop). */
const CREATOR_GRID_GAP = 24;

/** Makes a creator grid a size container, so its cards can react to how many columns it really has. */
export const creatorGridContainer = css`
	container: creator-grid / inline-size;
`;

/**
 * Styles for a grid too narrow for two cards side by side, i.e. one column. The desktop card is a fixed
 * 370:500 box with an inner scroller; stretched across a single wide column that ratio would make it taller
 * than the screen. A container query, so it follows the grid's width rather than the viewport's.
 */
export const mediaSingleColumn = (...args: Parameters<typeof css>) => css`
	@container creator-grid (max-width: ${CREATOR_CARD_MIN_WIDTH * 2 + CREATOR_GRID_GAP - 1}px) {
		${css(...args)}
	}
`;

/** A shortened phone label: the full label shows above the phone breakpoint, this one at and below it. */
export const PhoneFullLabel = styled.span`
	${mediaPhone`
		display: none;
	`}
`;

export const PhoneShortLabel = styled.span`
	display: none;

	${mediaPhone`
		display: inline;
	`}
`;
